import {
  cioFacetsToUrlFilters,
  urlFiltersToCioFacets,
} from '~/grid/utils/cioUtils';
import slugify from '~/techstyle-shared/react-components/src/utils/slugify';

import { getNewQueryParamString, QueryParamBehavior } from './routing';

/**
 * Checks if the given facet name is a size facet.
 * Specific to Constructor data shape.
 *
 * @param {string} cioFacetName - The name of the facet to check.
 * @return {boolean} - Returns true if the facet name is a size facet.
 */
export const isCioSizeFacet = (cioFacetName) => {
  return cioFacetName?.startsWith('available_size_');
};

/**
 * Checks if the given facet name is a color facet.
 * Specific to Constructor data shape.
 *
 * @param {string} cioFacetName - The name of the facet to check.
 * @return {boolean} - Returns true if the facet name is a color facet.
 */
export const isCioColorFacet = (cioFacetName) => {
  return cioFacetName === 'tag_color_list';
};

/**
 * Updates the selected filters in the URL params, which we use as the source of truth for state management.
 * All data fetching is based on the URL params.
 *
 * @param {string} facetName - The name of the facet to update.
 * @param {string} facetOption - The option of the facet to update.
 * @param {Object} router - The router object for navigation.
 * @param {QueryParamBehavior} queryParamBehavior - The behavior for updating the query params. Default is TOGGLE.
 * @returns {void}
 */
export const updateFiltersInUrlParams = ({
  facetName,
  facetOption,
  router,
  queryParamBehavior = QueryParamBehavior.TOGGLE,
}) => {
  const [aspathname, query] = router.asPath.split('?');
  const newQueryParamString = getNewQueryParamString({
    query,
    newQueryParams: { [facetName]: facetOption },
    queryParamBehavior,
  });

  if (!newQueryParamString) {
    router.replace(router.pathname, aspathname, {
      shallow: true,
    });
    return;
  }

  router.replace(router.pathname, `${aspathname}?${newQueryParamString}`, {
    shallow: true,
  });
};

/**
 * Clears all CIO facets from the URL params, preserving other query params.
 * This is used when the user clears all filters.
 *
 * @param {Object} router - The router object for navigation.
 * @param {Array} allCioFacetNames - An array of all CIO facet names to be cleared.
 * @returns {void}
 */
export const clearAllCioFacetsFromUrlParams = ({
  router,
  allCioFacetNames,
}) => {
  const [aspathname, query] = router.asPath.split('?');
  const urlParams = new URLSearchParams(query);

  // Only remove cioFacets from query params
  // Preserve other query params like tabId and utm params
  const preservedQueryParams = {};
  urlParams.forEach((value, key) => {
    const mappedKey = urlFiltersToCioFacets[key] || key;

    if (!allCioFacetNames.includes(mappedKey)) {
      preservedQueryParams[mappedKey] = value;
    }
  });

  const newQueryParamString = new URLSearchParams(
    preservedQueryParams
  ).toString();

  if (!newQueryParamString) {
    router.replace(router.pathname, aspathname, {
      shallow: true,
    });
    return;
  }

  router.replace(router.pathname, `${aspathname}?${newQueryParamString}`, {
    shallow: true,
  });
};

/**
 * Returns the display name of the selected facet option.
 * If the selected facet is a size facet, it appends the selected facet display name.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.selectedFacetName - The name of the selected facet.
 * @param {string} params.selectedFacetDisplayName - The display name of the selected facet.
 * @param {string} params.selectedFacetOptionDisplayName - The display name of the selected facet option.
 * @returns {string} - The transformed display name of the selected facet option.
 *
 * @example
 * facetName          | displayName
 * -------------------|-----------------
 * available_size_top | S | 6 (Top Size)
 * tag_color_list     | Red
 */
export const getTransformedFacetOptionDisplayName = ({
  selectedFacetName,
  selectedFacetDisplayName,
  selectedFacetOptionDisplayName,
}) => {
  if (isCioSizeFacet(selectedFacetName)) {
    return `${selectedFacetOptionDisplayName} (${selectedFacetDisplayName})`;
  }

  return selectedFacetOptionDisplayName;
};

export const isolateSizeFacets = (cioFacets = []) => {
  const sizeFacets = [];
  const nonSizeFacets = [];

  cioFacets.forEach((facet) => {
    if (isCioSizeFacet(facet.name)) {
      sizeFacets.push(facet);
    } else {
      nonSizeFacets.push(facet);
    }
  });

  return { sizeFacets, nonSizeFacets };
};

/**
 * Group size facets together in the list of facets.
 * Size facets need to be grouped together for proper UI rendering and functionality.
 * All other facets are preserved in their original order.
 *
 * @param {Array} cioFacets - The list of facets to be grouped.
 * @returns {Array} - The list of facets with size facets grouped together.
 */
export const groupSizeFacets = (cioFacets = []) => {
  const firstSizeFacetIndex = cioFacets.findIndex((facet) =>
    isCioSizeFacet(facet.name)
  );

  const { sizeFacets, nonSizeFacets } = isolateSizeFacets(cioFacets);

  if (!sizeFacets.length) {
    return cioFacets;
  }

  const groupedSizeFacet = {
    display_name: 'Size', // needs to be localized via RB?
    name: 'size',
    sub_facets: sizeFacets,
  };

  return nonSizeFacets?.toSpliced(firstSizeFacetIndex, 0, groupedSizeFacet);
};

/**
 *  We can't use the `selected` directly from cioFacets from the CIO API response, due to size filters
 *  being in cio prefilter expressions instead of the filters param. This results in the size filters
 *  not having the selected value in the response. We have to handle selected filters
 *  by checking the URL params as our source of truth.
 *
 * @param {string} facetName - The name of the facet.
 * @param {string} facetOption - The value of the facet option.
 * @param {Object} selectedUrlFilters - The current URL filters.
 *
 * @returns {boolean} - Returns true if the facet option is selected, false otherwise.
 */
export const isFacetOptionSelected = ({
  facetName,
  facetOption,
  selectedUrlFilters,
}) => {
  if (!selectedUrlFilters || !facetName || !facetOption) {
    return false;
  }

  const urlFacetName = cioFacetsToUrlFilters[facetName] || facetName;
  return Boolean(selectedUrlFilters?.[urlFacetName]?.includes(facetOption));
};

/**
 * Counts the number of selected options in a facet.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.name - The name of the facet.
 * @param {Array} params.subFacets - The sub-facets of the facet. Does not exist for all facets. Mainly for grouped size facets.
 * @param {Array} params.options - The options of the facet.
 * @param {Object} params.selectedUrlFilters - The current URL filters.
 * @returns {number} - The count of selected options.
 */
export const getSelectedOptionCount = ({
  name,
  subFacets,
  options = [],
  selectedUrlFilters,
}) => {
  if (subFacets) {
    return subFacets.reduce((total, subFacet) => {
      return subFacet?.options?.reduce((subTotal, subFacetOption) => {
        return isFacetOptionSelected({
          facetName: subFacet?.name,
          facetOption: subFacetOption?.value,
          selectedUrlFilters,
        })
          ? subTotal + 1
          : subTotal;
      }, total);
    }, 0);
  }

  return options?.reduce((total, option) => {
    return isFacetOptionSelected({
      facetName: name,
      facetOption: option?.value,
      selectedUrlFilters,
    })
      ? total + 1
      : total;
  }, 0);
};

/**
 * Returns the all of the CIO facets based on the URL filters.
 * (Some are only applied via auto-size filters and not via the UI selectors)
 * This is needed because the size filters are not included in the CIO API response,
 * due to them being in the prefilter expressions instead of the filters param.
 * We need to handle selected filters by checking the URL params as our source of truth.
 *
 * Note: Since we are using `allCioFacets` to get the selected size facets, the `count` property
 * is not accurate for the current results of the grid. We are currently not using the `count` property
 * so this is not a problem, but be aware of this technical limitation when using size filters in prefilter expressions.
 *
 * @param {Object} params - The parameters for the function.
 * @param {Object} params.selectedUrlFilters - The current URL filters.
 * @param {Array} params.allCioFacets - The list of all CIO facets.
 * @param {Array} params.cioFacets - The list of CIO facets.
 * @returns {Array} - The list of selected CIO size facets.
 */
export const getAllCioFacetsWithSelectedUrlFilters = ({
  selectedUrlFilters,
  allCioFacets = [],
  cioFacets = [],
}) => {
  const selectedCioSizeFacets = Object.entries(selectedUrlFilters).reduce(
    (acc, [key, value]) => {
      const cioFacetName = urlFiltersToCioFacets[key] || key;
      const cioFacet = allCioFacets?.find(
        (facet) => facet?.name === cioFacetName
      );

      // Ignore all non-size facets
      if (!isCioSizeFacet(cioFacetName) || !cioFacet) {
        return acc;
      }

      const selectedSizeOptions = value?.map((option) => {
        const cioOption = cioFacet?.options?.find(
          (facetOption) => facetOption?.value === option
        );
        return cioOption;
      });

      const currentCioFacetOptions = cioFacets?.find(
        (facet) => facet?.name === cioFacetName
      )?.options;

      // Filter out the selected size options that are already in the currentCioFacetOptions
      // We only want to show the selected size options that are not already in the currentCioFacetOptions
      const filteredSelectedSizeOptions = selectedSizeOptions
        ?.filter(
          (option) =>
            !currentCioFacetOptions?.some((o) => o.value === option.value)
        )
        ?.filter(Boolean);

      // Don't add the facet if no selected options are found
      if (!filteredSelectedSizeOptions?.length) {
        return acc;
      }

      return [
        ...acc,
        {
          ...cioFacet,
          options: filteredSelectedSizeOptions,
        },
      ];
    },
    []
  );

  // We prioritize showing selected size facets if some are found in the URL params and not in the cioFacets
  const orderedCioFacets = [...selectedCioSizeFacets, ...cioFacets];

  return orderedCioFacets;
};

/**
 * Returns a mapped value from `cioFacetsToUrlFilters` if available
 * or removes 'tag_' and '_list' from the given string.
 * Converts result to use kebab case.
 *
 * @param {string} filterName - The input string to sanitize.
 *
 * @returns {string} The sanitized string in kebab case.
 */
export const sanitizeFilterName = (filterName) => {
  if (typeof filterName !== 'string') {
    return '';
  }

  const result =
    cioFacetsToUrlFilters[filterName] ||
    filterName.replace(/^tag_/, '').replace(/_list$/, '');
  return slugify(result, '-');
};

/**
 * Generates a slugified version of the selected CIO facet and option display name.
 * This is used to create a unique RB identifier for the selected facet option.
 *
 * @param {string} facetName - The name of the facet.
 * @param {string} facetOptionDisplayName - The display name of the facet option.
 * @returns {string} - The slugified version of the selected CIO facet and option display name.
 */
export const getSelectedCioFacetRbid = (facetName, facetOptionDisplayName) => {
  const mappedFacetName = cioFacetsToUrlFilters[facetName] || facetName;
  return slugify(`${mappedFacetName}_${facetOptionDisplayName}`);
};
